/**
 * confirmDialog(message, opts) → Promise<boolean>
 *
 * Replacement for window.confirm() inside the embedded admin. Native
 * dialogs in cross-origin iframes are on Chromium's removal track and are
 * already suppressed by some browsers/webviews — when suppressed,
 * confirm() returns false, so "Cancel this generation?" silently did
 * nothing and "Delete this chat?" could never delete. This renders a
 * Polaris Modal via a host mounted once in AppLayout (same window-event
 * pattern as the toasts), so any page can `await confirmDialog(...)`.
 *
 * Falls back to window.confirm when no host is mounted (e.g. the public
 * /r/:code page) so behaviour is never worse than before.
 */
import React, { useEffect, useState } from 'react';
import { Modal, Text } from '@shopify/polaris';

const CONFIRM_EVENT = 'autonoma:confirm';
let hostMounted = false;

export function confirmDialog(message, opts = {}) {
  if (!hostMounted || typeof window === 'undefined') {
    // eslint-disable-next-line no-alert
    return Promise.resolve(window.confirm(message));
  }
  return new Promise((resolve) => {
    window.dispatchEvent(new CustomEvent(CONFIRM_EVENT, {
      detail: {
        message,
        title: opts.title || 'Are you sure?',
        confirmLabel: opts.confirmLabel || 'Yes',
        cancelLabel: opts.cancelLabel || 'Keep going',
        destructive: opts.destructive !== false,
        resolve,
      },
    }));
  });
}

export function ConfirmDialogHost() {
  const [req, setReq] = useState(null);

  useEffect(() => {
    hostMounted = true;
    const onConfirm = (e) => {
      // A second request while one is open answers the first "no".
      setReq((prev) => { prev?.resolve(false); return e.detail; });
    };
    window.addEventListener(CONFIRM_EVENT, onConfirm);
    return () => {
      hostMounted = false;
      window.removeEventListener(CONFIRM_EVENT, onConfirm);
    };
  }, []);

  if (!req) return null;
  const answer = (value) => {
    req.resolve(value);
    setReq(null);
  };

  return (
    <Modal
      open
      onClose={() => answer(false)}
      title={req.title}
      primaryAction={{ content: req.confirmLabel, destructive: req.destructive, onAction: () => answer(true) }}
      secondaryActions={[{ content: req.cancelLabel, onAction: () => answer(false) }]}
    >
      <Modal.Section>
        <Text as="p">{req.message}</Text>
      </Modal.Section>
    </Modal>
  );
}

export default confirmDialog;
